import React, { useState } from 'react'
import { Box } from '@mui/material'
import dayjs from 'dayjs'

// Tiny dependency-free area sparkline. Hovering shows the value at the nearest
// daily point (with a crosshair + marker dot), and the date when `days` is
// supplied. Shared by the KPI cards and the dealer "Boats" performance table.
const ALARM_COLOR = '#F59E0B' // orange
const DTC_COLOR = '#E00000' // red

export const Sparkline: React.FC<{
    data: number[]
    color: string
    fmt?: (v: number) => string
    days?: string[]
    /** Per-day alarm / DTC counts (aligned to `data`): orange / red markers. */
    alarms?: number[]
    dtcs?: number[]
    /** Render the hover tooltip BELOW the point (e.g. top table row, where an
     *  above-placed tooltip would be clipped by the tabs/header). */
    flipTip?: boolean
    /** Extra tooltip content for day `i` (e.g. engine-hour min/max + fault log
     *  in the Metrics tab). Rendered below the value, allowed to wrap. */
    tipExtra?: (i: number) => React.ReactNode
    /** Override the date label shown above the value (default is daily
     *  `ddd, MMM D`). Used for monthly series, e.g. `MMM 'YY`. */
    labelFmt?: (raw: string) => string
}> = React.memo(function Sparkline({
    data,
    color,
    fmt,
    days,
    alarms,
    dtcs,
    flipTip,
    tipExtra,
    labelFmt,
}) {
    const [hi, setHi] = useState<number | null>(null)
    if (!data || data.length < 2) return <Box sx={{ height: 34 }} />
    const w = 120
    const h = 34
    const min = Math.min(...data)
    const max = Math.max(...data)
    const range = max - min || 1
    const pts = data.map((v, i) => {
        const x = (i / (data.length - 1)) * w
        const y = h - ((v - min) / range) * (h - 4) - 2
        return [x, y] as const
    })
    const line = pts.map((p) => `${p[0]},${p[1]}`).join(' ')
    const area = `0,${h} ${line} ${w},${h}`
    const fmtVal = fmt || ((v: number) => v.toLocaleString())
    // Secondary axis: per-day alarm+DTC count, drawn as stacked bars scaled to
    // the busiest fault day (independent of the engine-hours axis).
    const maxFaults = Math.max(
        1,
        ...data.map((_, i) => (alarms?.[i] || 0) + (dtcs?.[i] || 0))
    )
    const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect()
        const ratio = rect.width ? (e.clientX - rect.left) / rect.width : 0
        setHi(
            Math.max(
                0,
                Math.min(
                    data.length - 1,
                    Math.round(ratio * (data.length - 1))
                )
            )
        )
    }
    const pctX = hi != null ? (pts[hi][0] / w) * 100 : 0
    const pctY = hi != null ? (pts[hi][1] / h) * 100 : 0
    return (
        <Box
            sx={{ position: 'relative', cursor: 'crosshair' }}
            onMouseMove={onMove}
            onMouseLeave={() => setHi(null)}
        >
            <svg
                width="100%"
                height={h}
                viewBox={`0 0 ${w} ${h}`}
                preserveAspectRatio="none"
                style={{ display: 'block' }}
            >
                <polygon points={area} fill={color} opacity={0.15} />
                <polyline
                    points={line}
                    fill="none"
                    stroke={color}
                    strokeWidth={1.5}
                    vectorEffect="non-scaling-stroke"
                />
                {hi != null && (
                    <line
                        x1={pts[hi][0]}
                        y1={0}
                        x2={pts[hi][0]}
                        y2={h}
                        stroke={color}
                        strokeWidth={1}
                        opacity={0.45}
                        vectorEffect="non-scaling-stroke"
                    />
                )}
            </svg>
            {/* Fault dots (secondary axis): vertical position = that day's
                alarm+DTC count scaled to the busiest fault day. Orange = alarm,
                red = DTC, split = both. HTML so they stay round. */}
            {(alarms || dtcs) &&
                data.map((_, i) => {
                    const a = alarms?.[i] || 0
                    const t = dtcs?.[i] || 0
                    const total = a + t
                    if (!total) return null
                    const left = (i / (data.length - 1)) * 100
                    // Higher count → higher up (band 8%–92% so it never clips).
                    const topPct = 8 + (1 - total / maxFaults) * 84
                    const bg =
                        a && t
                            ? `linear-gradient(90deg, ${ALARM_COLOR} 0 50%, ${DTC_COLOR} 50% 100%)`
                            : a
                            ? ALARM_COLOR
                            : DTC_COLOR
                    return (
                        <Box
                            key={`fd${i}`}
                            sx={{
                                position: 'absolute',
                                left: `${left}%`,
                                top: `${topPct}%`,
                                transform: 'translate(-50%,-50%)',
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: bg,
                                border: '1px solid rgba(0,0,0,0.45)',
                                pointerEvents: 'none',
                            }}
                        />
                    )
                })}
            {hi != null && (
                <>
                    {/* Marker dot rendered in HTML so it stays round despite the
                        SVG's horizontal stretch (preserveAspectRatio="none"). */}
                    <Box
                        sx={{
                            position: 'absolute',
                            left: `${pctX}%`,
                            top: `${pctY}%`,
                            transform: 'translate(-50%,-50%)',
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: color,
                            border: '1px solid #fff',
                            pointerEvents: 'none',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            ...(flipTip
                                ? { top: '100%', mt: 0.5 }
                                : { bottom: '100%', mb: 0.5 }),
                            left: `${pctX}%`,
                            transform: 'translateX(-50%)',
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: 'rgba(18,20,23,0.97)',
                            border: '1px solid rgba(255,255,255,0.12)',
                            color: '#fff',
                            fontSize: 11,
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            zIndex: 5,
                            textAlign: 'center',
                            lineHeight: 1.35,
                        }}
                    >
                        {days && days[hi] && (
                            <Box
                                component="span"
                                sx={{
                                    display: 'block',
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: 'rgba(255,255,255,0.6)',
                                }}
                            >
                                {labelFmt
                                    ? labelFmt(days[hi])
                                    : dayjs(days[hi]).format('ddd, MMM D')}
                            </Box>
                        )}
                        {fmtVal(data[hi])}
                        {(alarms?.[hi] || 0) > 0 && (
                            <Box
                                component="span"
                                sx={{
                                    display: 'block',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: ALARM_COLOR,
                                }}
                            >
                                {alarms![hi]} alarm
                                {alarms![hi] > 1 ? 's' : ''}
                            </Box>
                        )}
                        {(dtcs?.[hi] || 0) > 0 && (
                            <Box
                                component="span"
                                sx={{
                                    display: 'block',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: DTC_COLOR,
                                }}
                            >
                                {dtcs![hi]} DTC{dtcs![hi] > 1 ? 's' : ''}
                            </Box>
                        )}
                        {tipExtra && (
                            <Box
                                sx={{
                                    mt: 0.5,
                                    pt: 0.5,
                                    borderTop:
                                        '1px solid rgba(255,255,255,0.12)',
                                    fontWeight: 500,
                                    whiteSpace: 'normal',
                                    maxWidth: 220,
                                }}
                            >
                                {tipExtra(hi)}
                            </Box>
                        )}
                    </Box>
                </>
            )}
        </Box>
    )
})

export default Sparkline
